<template>
  <div v-if="order">
    <div class="orderactions">
      <h2>{{ $t('components.order.actions.title') }}</h2>

      <div v-if="hasGusProducts && !isOrderAbandoned" class="action action-sendtogus">
        <h4 class="h3">{{ $t('components.order.actions.sendtogus.title') }}</h4>
        <div v-if="!isTransmittedToGus">
          <div>{{ $t('components.order.actions.sendtogus.nottransmitted') }}</div>
          <div class="mt-3">
            <b-button class="action-button mr-2" variant="success" @click="transmitToGusAsOrder()">
              <loader :inline="true" v-if="isChangingTransmissionState" /><b-icon v-else icon="arrow-right" /> {{ $t('components.order.actions.sendtogus.buttonorder') }}
            </b-button>
            <b-button class="action-button mr-2" variant="info" @click="transmitToGusAsOffer()">
              <loader :inline="true" v-if="isChangingTransmissionState" /><b-icon v-else icon="arrow-right" /> {{ $t('components.order.actions.sendtogus.buttonoffer') }}
            </b-button>
            <b-button class="action-button mr-2" variant="warning" @click="setGusOrdersTransmitted()">
              <loader :inline="true" v-if="isChangingTransmissionState" /><b-icon v-else icon="arrow-right" /> {{ $t('components.order.actions.sendtogus.buttondonttransmit') }}
            </b-button>
          </div>
        </div>
        <div v-else>
          <b-button class="action-button" variant="dark">{{ $t('components.order.actions.sendtogus.transmittedat') }} {{ transmittedtoGusDate }}</b-button>
        </div>
      </div>

      <div v-if="hasRuefenachtProducts && !isOrderAbandoned" class="action action-sendtodruefenacht">
        <h4 class="h3">{{ $t('components.order.actions.sendtoruefenacht.title') }}</h4>
        <div>
          <div v-if="!isRuefenachtTransmitted">
            <div>{{ $t('components.order.actions.sendtoruefenacht.nottransmitted') }}</div>
            <div class="mt-3">
              <b-button class="action-button mr-2" variant="success" @click="transmitToRuefenachtAsOrder()">
                <loader :inline="true" v-if="isChangingToRuefenachtTransmissionState" /><b-icon v-else icon="arrow-right" /> {{ $t('components.order.actions.sendtoruefenacht.generatemail') }}
              </b-button>
              <b-button class="action-button mr-2" variant="warning" @click="setRuefenachtAstransmitted()">
                <loader :inline="true" v-if="isChangingToRuefenachtTransmissionState" /><b-icon v-else icon="arrow-right" /> {{ $t('components.order.actions.sendtoruefenacht.button') }}
              </b-button>
            </div>
          </div>
          <div v-else>
            <b-button class="action-button" variant="dark">{{ $t('components.order.actions.sendtoruefenacht.transmittedat') }} {{ transmittedRuefenachtDate }}</b-button>
          </div>
        </div>
      </div>

      <div v-if="hasOnlyUndefinedWarehouseProducts && !isOrderAbandoned" class="action action-sendtodruefenacht">
        <h4 class="h3">{{ $t('components.order.actions.notransmission.title') }}</h4>
        <div>
          <div> <b-alert variant="danger" show>{{ $t('components.order.actions.notransmission.reason') }}</b-alert></div><br />
          <div v-if="!isUndefinedWarehouseTransmitted">
            <b-button class="action-button" variant="success" @click="setUndefinedWarehouseAstransmitted()">
              <loader :inline="true" v-if="isChangingNoWarehouseTransmissionState" /><b-icon v-else icon="arrow-right" /> {{ $t('components.order.actions.nowarehouse.button') }}
            </b-button>
          </div>
          <div v-else>
            <b-button class="action-button" variant="dark">{{ $t('components.order.actions.nowarehouse.transmittedat') }} {{ transmittedUndefinedWarehouseDate }}</b-button>
          </div>
        </div>
      </div>

      <div v-if="hasSomeUndefinedWarehouseProducts && !hasOnlyUndefinedWarehouseProducts && !isOrderAbandoned" class="action action-sendtodruefenacht">
        <h4 class="h3">{{ $t('components.order.actions.nowarehouse.title') }}</h4>
        <div v-if="!isUndefinedWarehouseTransmitted">
          <div> <b-alert variant="danger" show>{{ $t('components.order.actions.nowarehouse.reason') }}</b-alert></div><br />
          <b-button class="action-button" variant="success" @click="setUndefinedWarehouseAstransmitted()">
            <loader :inline="true" v-if="isChangingNoWarehouseTransmissionState" /><b-icon v-else icon="arrow-right" /> {{ $t('components.order.actions.nowarehouse.button') }}
          </b-button>
        </div>
        <div v-else>
          <b-button class="action-button" variant="dark">{{ $t('components.order.actions.nowarehouse.transmittedat') }} {{ transmittedUndefinedWarehouseDate }}</b-button>
        </div>
      </div>

      <div v-if="!hasAnyActions" class="action action-noactions">
        <b-alert variant="info" show>{{ $t('components.order.actions.noactions') }}</b-alert>
      </div>
    </div>
  </div>
</template>

<script>
import { ORDER_TRANSMISSIONSTATES, ORDER_TRANSMISSIONTYPES, WAREHOUSES } from '@/assets/js/config/server';
import Loader from '@/components/Loader';

export default {
  name: 'OrderActions',
  components: {
    Loader
  },
  props: {
    order: {
      type: Object,
      default: null
    },
    entityKey: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isChangingTransmissionState: false,
      isChangingNoWarehouseTransmissionState: false,
      isChangingToRuefenachtTransmissionState: false
    }
  },
  computed: {
    isOrderAbandoned() {
      return this.order?.orderState === 'Abandoned'
    },
    hasAnyActions() {
      if (this.isOrderAbandoned) {
        return false
      }
      return this.hasGusProducts ||
        this.hasRuefenachtProducts ||
        this.hasOnlyUndefinedWarehouseProducts ||
        (this.hasSomeUndefinedWarehouseProducts && !this.hasOnlyUndefinedWarehouseProducts)
    },
    orderItems() {
      // FullOrderModel has items at root level, or in orderedBasket.items
      // Support both old (executedBasket) and new (orderedBasket/items) structures
      if (!this.order) {
        return []
      }
      if (this.order.items && Array.isArray(this.order.items)) {
        return this.order.items
      } else if (this.order.orderedBasket && this.order.orderedBasket.items) {
        return this.order.orderedBasket.items
      } else if (this.order.executedBasket && this.order.executedBasket.items) {
        return this.order.executedBasket.items
      }
      return []
    },
    transmissions() {
      // FullOrderModel might not have transmissions property
      const rawTransmissions = this.order?.transmissions || {}

      // Normalize keys to PascalCase to handle both camelCase and PascalCase from API
      // API may return camelCase keys (e.g., "transmitNotPossible") but frontend expects PascalCase (e.g., "TransmitNotPossible")
      const normalized = {}
      for (const key in rawTransmissions) {
        if (Object.prototype.hasOwnProperty.call(rawTransmissions, key)) {
          // Convert first character to uppercase if it's lowercase (camelCase -> PascalCase)
          const normalizedKey = key.length > 0 && key[0] === key[0].toLowerCase()
            ? key[0].toUpperCase() + key.slice(1)
            : key
          normalized[normalizedKey] = rawTransmissions[key]
        }
      }
      return normalized
    },
    hasGusProducts() {
      if (!this.order || !this.transmissions || Object.keys(this.transmissions).length === 0) {
        return false
      }

      return Object.prototype.hasOwnProperty.call(this.transmissions, ORDER_TRANSMISSIONTYPES.externalOrderManagement) && this.hasActiveItemsWithWareHouse(WAREHOUSES.ebipharm)
    },
    hasRuefenachtProducts() {
      if (!this.order || !this.transmissions || Object.keys(this.transmissions).length === 0) {
        return false
      }

      return Object.prototype.hasOwnProperty.call(this.transmissions, ORDER_TRANSMISSIONTYPES.email) && this.hasActiveItemsWithWareHouse(WAREHOUSES.ruefenacht)
    },
    hasOnlyUndefinedWarehouseProducts() {
      if (!this.order) {
        return false
      }
      const items = this.orderItems.filter(itm => itm.state === 'Active' || itm.state === 'active')
      return items.length && items.every(item => item.product && item.product.warehouse === undefined)
    },
    hasSomeUndefinedWarehouseProducts() {
      if (!this.order || !this.transmissions || Object.keys(this.transmissions).length === 0) {
        return false
      }

      return Object.prototype.hasOwnProperty.call(this.transmissions, ORDER_TRANSMISSIONTYPES.none) && this.hasActiveItemsWithWareHouse(undefined)
    },
    isTransmittedToGus() {
      if (!this.transmissions || !this.transmissions[ORDER_TRANSMISSIONTYPES.externalOrderManagement]) {
        return false
      }
      const transmission = this.transmissions[ORDER_TRANSMISSIONTYPES.externalOrderManagement]
      // Handle both camelCase and PascalCase property names
      const state = transmission.state || transmission.State
      return state && state !== ORDER_TRANSMISSIONSTATES.notTransmitted
    },
    isUndefinedWarehouseTransmitted() {
      if (!this.transmissions || !this.transmissions[ORDER_TRANSMISSIONTYPES.none]) {
        return false
      }
      const transmission = this.transmissions[ORDER_TRANSMISSIONTYPES.none]
      // Handle both camelCase and PascalCase property names
      const state = transmission.state || transmission.State
      return state && state !== ORDER_TRANSMISSIONSTATES.notTransmitted
    },
    isRuefenachtTransmitted() {
      if (!this.transmissions || !this.transmissions[ORDER_TRANSMISSIONTYPES.email]) {
        return false
      }
      const transmission = this.transmissions[ORDER_TRANSMISSIONTYPES.email]
      // Handle both camelCase and PascalCase property names
      const state = transmission.state || transmission.State
      return state && state !== ORDER_TRANSMISSIONSTATES.notTransmitted
    },
    transmittedtoGusDate() {
      if (!this.transmissions || !this.transmissions[ORDER_TRANSMISSIONTYPES.externalOrderManagement]) {
        return ''
      }
      const transmission = this.transmissions[ORDER_TRANSMISSIONTYPES.externalOrderManagement]
      const timestamp = transmission.timestamp || transmission.Timestamp
      return timestamp ? new Date(timestamp).toLocaleString() : ''
    },
    transmittedUndefinedWarehouseDate() {
      if (!this.transmissions || !this.transmissions[ORDER_TRANSMISSIONTYPES.none]) {
        return ''
      }
      const transmission = this.transmissions[ORDER_TRANSMISSIONTYPES.none]
      const timestamp = transmission.timestamp || transmission.Timestamp
      return timestamp ? new Date(timestamp).toLocaleString() : ''
    },
    transmittedRuefenachtDate() {
      if (!this.transmissions || !this.transmissions[ORDER_TRANSMISSIONTYPES.email]) {
        return ''
      }
      const transmission = this.transmissions[ORDER_TRANSMISSIONTYPES.email]
      const timestamp = transmission.timestamp || transmission.Timestamp
      return timestamp ? new Date(timestamp).toLocaleString() : ''
    },
    getRuefenachtProducts() {
      return this.orderItems.filter(item => item.product && item.product.warehouse === WAREHOUSES.ruefenacht && (item.state === 'Active' || item.state === 'active')).map(item => ({
        id: item.product.id,
        name: item.product.name,
        quantity: item.quantity,
        deliveryweek: this.getAdditionalConfiguration(item)
      }))
    }
  },
  methods: {
    hasActiveItemsWithWareHouse(warehouse) {
      return this.orderItems.some(itm => (itm.state === 'Active' || itm.state === 'active') && itm.product && itm.product.warehouse === warehouse)
    },
    transmitToGusAsOrder() {
      this.isChangingTransmissionState = true
      this.$store.dispatch(`${this.entityKey}/triggerSetTransmissionState`, {
        entityId: this.order.id,
        transmissionType: ORDER_TRANSMISSIONTYPES.externalOrderManagement,
        transmissionState: ORDER_TRANSMISSIONSTATES.pendingTransmission,
        treatAsOffer: false
      })
        .finally(() => {
          this.isChangingTransmissionState = false
        })
    },
    transmitToGusAsOffer() {
      this.isChangingTransmissionState = true
      this.$store.dispatch(`${this.entityKey}/triggerSetTransmissionState`, {
        entityId: this.order.id,
        transmissionType: ORDER_TRANSMISSIONTYPES.externalOrderManagement,
        transmissionState: ORDER_TRANSMISSIONSTATES.pendingTransmission,
        treatAsOffer: true
      })
        .finally(() => {
          this.isChangingTransmissionState = false
        })
    },
    setGusOrdersTransmitted() {
      this.isChangingTransmissionState = true
      this.$store.dispatch(`${this.entityKey}/triggerSetTransmissionState`, {
        entityId: this.order.id,
        transmissionType: ORDER_TRANSMISSIONTYPES.externalOrderManagement,
        transmissionState: ORDER_TRANSMISSIONSTATES.transmitted,
        treatAsOffer: false
      })
        .finally(() => {
          this.isChangingTransmissionState = false
        })
    },
    setUndefinedWarehouseAstransmitted() {
      this.isChangingNoWarehouseTransmissionState = true
      this.$store.dispatch(`${this.entityKey}/triggerSetTransmissionState`, {
        entityId: this.order.id,
        transmissionType: ORDER_TRANSMISSIONTYPES.none,
        transmissionState: ORDER_TRANSMISSIONSTATES.transmitted
      })
        .finally(() => {
          this.isChangingNoWarehouseTransmissionState = false
        })
    },
    transmitToRuefenachtAsOrder() {
      this.isChangingTransmissionState = true
      this.$store.dispatch(`${this.entityKey}/triggerSetTransmissionState`, {
        entityId: this.order.id,
        transmissionType: ORDER_TRANSMISSIONTYPES.email,
        transmissionState: ORDER_TRANSMISSIONSTATES.pendingTransmission,
        treatAsOffer: true
      })
        .finally(() => {
          this.isChangingTransmissionState = false
        })
    },
    setRuefenachtAstransmitted() {
      this.isChangingToRuefenachtTransmissionState = true
      this.$store.dispatch(`${this.entityKey}/triggerSetTransmissionState`, {
        entityId: this.order.id,
        transmissionType: ORDER_TRANSMISSIONTYPES.email,
        transmissionState: ORDER_TRANSMISSIONSTATES.transmitted
      })
        .finally(() => {
          this.isChangingToRuefenachtTransmissionState = false
        })
    },
    getAdditionalConfiguration(item) {
      if (item.configuration && item.configuration.additionalConfigurations && item.configuration.additionalConfigurations.dateStart && item.configuration.additionalConfigurations.dateEnd) {
        return `${this.$options.filters.formatDate(item.configuration.additionalConfigurations.dateStart)} - ${this.$options.filters.formatDate(item.configuration.additionalConfigurations.dateEnd)}`
      }
      return null
    }
  }
}
</script>

<style lang="scss">
$orderactions-orderactions-gap: $spacer * 3 !default;
$orderactions-orderactions-inner-gap: $orderactions-orderactions-gap * 0.5 !default;
$orderactions-orderactions-border: $border-width solid $border-color !default;

$orderactions-action-action-gap: $spacer * 2 !default;
$orderactions-action-action-border: $border-width solid $border-color !default;

$orderactions-action-info-gap: $spacer * 0.5 !default;
$orderactions-action-info-font-size: $font-size-sm !default;
$orderactions-action-info-font-weight: inherit !default;
$orderactions-action-info-color: inherit !default;
$orderactions-action-info-time-font-size: inherit !default;
$orderactions-action-info-time-font-weight: $font-weight-bold !default;
$orderactions-action-info-time-color: inherit !default;

$orderactions-action-description-gap: $spacer !default;
$orderactions-action-description-font-size: $font-size-sm !default;

.orderactions {
  .action {
    .action-info {
      margin-bottom: $orderactions-action-info-gap;
      font-size: $orderactions-action-info-font-size;
      font-weight: $orderactions-action-info-font-weight;
      color: $orderactions-action-info-color;

      span,
      time {
        font-size: $orderactions-action-info-time-font-size;
        font-weight: $orderactions-action-info-time-font-weight;
        color: $orderactions-action-info-time-color;
      }
    }

    .action-description {
      display: inline-block;
      margin-bottom: $orderactions-action-description-gap;
      font-size: $orderactions-action-description-font-size;
    }

    .action-button {}

    + .action {
      margin-top: $orderactions-action-action-gap * 0.5;
      padding-top: $orderactions-action-action-gap * 0.5;
      border-top: $orderactions-action-action-border;
    }
  }

  + .orderactions {
    margin-top: $orderactions-orderactions-gap;
    padding-top: $orderactions-orderactions-inner-gap;
    border-top: $orderactions-orderactions-border;
  }
}
</style>
